// ABOUTME: Modal component for creating new workouts with exercise management
// ABOUTME: Allows users to add workout details and multiple exercises with various parameters

import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { WorkoutData, Exercise, DataItem, WorkoutTemplateData } from '../types';
import { StorageManager } from '../services/storage/StorageManager';

interface AddWorkoutModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (workout: {
        name: string;
        description: string;
        imageUrl: string;
        category: 'workout';
        timestamp: number;
        data: WorkoutData;
    }) => void;
    editingItem?: DataItem | null;
}

const AddWorkoutModal: React.FC<AddWorkoutModalProps> = ({ isOpen, onClose, onSave, editingItem }) => {
    const [name, setName] = useState('');
    const [duration, setDuration] = useState(30);
    const [calories, setCalories] = useState(200);
    const [type, setType] = useState<'strength' | 'cardio' | 'flexibility' | 'mixed'>('strength');
    const [exercises, setExercises] = useState<Exercise[]>([
        { name: '', setNumber: 1, reps: 10, weight: 0 }
    ]);
    const [availableTemplates, setAvailableTemplates] = useState<DataItem[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<string>('');
    const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [expandedExercises, setExpandedExercises] = useState<Set<number>>(new Set());
    const [cloneCount, setCloneCount] = useState<number>(2);
    const [workoutDate, setWorkoutDate] = useState('');
    const [workoutTime, setWorkoutTime] = useState('');
    const [isBasicInfoExpanded, setIsBasicInfoExpanded] = useState(false);

    // Load templates when modal opens
    useEffect(() => {
        if (isOpen) {
            loadTemplates();
        }
    }, [isOpen]);

    const loadTemplates = async () => {
        try {
            const provider = await StorageManager.getProvider();
            const templates = await provider.getItems(1, 50, { filterBy: 'workout_template' });
            setAvailableTemplates(templates);
        } catch (error) {
            console.error('Failed to load workout templates:', error);
        }
    };

    const handleTemplateSelection = (templateId: string) => {
        if (!templateId) {
            setSelectedTemplate('');
            return;
        }

        const template = availableTemplates.find(t => t.id.toString() === templateId);
        if (template && template.data) {
            const templateData = template.data as WorkoutTemplateData;
            setName(template.name);
            setDuration(templateData.duration);
            setCalories(templateData.calories);
            setType(templateData.type);
            setExercises(templateData.exercises.length > 0 ? [...templateData.exercises] : [{ name: '', sets: 1, reps: 10, weight: 0 }]);
            setSelectedTemplate(templateId);
        }
    };

    const saveAsTemplate = async () => {
        if (!templateName.trim()) return;

        try {
            const provider = await StorageManager.getProvider();
            const templateData: WorkoutTemplateData = {
                exercises: exercises.filter(ex => ex.name.trim()),
                duration,
                calories,
                type,
                difficulty: 'intermediate',
                tags: [type],
                description: `Template created from workout: ${name}`
            };

            await provider.addItem({
                name: templateName.trim(),
                description: templateData.description,
                imageUrl: '',
                category: 'workout_template',
                timestamp: Date.now(),
                data: templateData
            });

            setTemplateName('');
            setShowSaveAsTemplate(false);
            await loadTemplates(); // Refresh templates list
        } catch (error) {
            console.error('Failed to save workout template:', error);
        }
    };

    // Reset form when modal opens/closes or when switching between add/edit modes
    useEffect(() => {
        if (isOpen) {
            if (editingItem && editingItem.category === 'workout') {
                try {
                    // Pre-populate form with existing data
                    const workoutData = editingItem.data as WorkoutData;
                    setName(editingItem.name || '');
                    setDuration(workoutData?.duration || 30);
                    setCalories(workoutData?.calories || 200);
                    setType(workoutData?.type || 'strength');
                    
                    // Ensure exercises array is properly formatted
                    if (workoutData?.exercises && Array.isArray(workoutData.exercises) && workoutData.exercises.length > 0) {
                        setExercises(workoutData.exercises);
                    } else {
                        setExercises([{ name: '', setNumber: 1, reps: 10, weight: 0 }]);
                    }
                    
                    // Set date and time from existing timestamp
                    const date = new Date(editingItem.timestamp);
                    setWorkoutDate(date.toISOString().split('T')[0]);
                    setWorkoutTime(date.toTimeString().slice(0, 5));
                } catch (error) {
                    console.error('Error loading workout data:', error);
                    // Set defaults if there's an error
                    setName(editingItem.name || '');
                    setDuration(30);
                    setCalories(200);
                    setType('strength');
                    setExercises([{ name: '', setNumber: 1, reps: 10, weight: 0 }]);
                    
                    const now = new Date();
                    setWorkoutDate(now.toISOString().split('T')[0]);
                    setWorkoutTime(now.toTimeString().slice(0, 5));
                }
            } else {
                // Reset to empty form for new workout
                setName('');
                setDuration(30);
                setCalories(200);
                setType('strength');
                setExercises([{ name: '', setNumber: 1, reps: 10, weight: 0 }]);
                setSelectedTemplate('');
                setShowSaveAsTemplate(false);
                setTemplateName('');
                
                // Set current date and time for new workout
                const now = new Date();
                setWorkoutDate(now.toISOString().split('T')[0]);
                setWorkoutTime(now.toTimeString().slice(0, 5));
            }
            // Reset expanded states when modal opens
            setExpandedExercises(new Set());
            // Set basic info expanded state - collapsed when editing, expanded when creating new
            setIsBasicInfoExpanded(!editingItem);
        }
    }, [isOpen, editingItem]);

    const handleAddExercise = () => {
        const nextSetNumber = exercises.length > 0 ? Math.max(...exercises.map(ex => ex.setNumber || 1)) + 1 : 1;
        setExercises([...exercises, { name: '', setNumber: nextSetNumber, reps: 10, weight: 0 }]);
    };

    const handleCloneExercise = (index: number) => {
        const exerciseToClone = exercises[index];
        let nextSetNumber = exercises.length > 0 ? Math.max(...exercises.map(ex => ex.setNumber || 1)) + 1 : 1;
        const newExercises = [...exercises];
        
        // Create multiple clones based on cloneCount
        for (let i = 0; i < cloneCount; i++) {
            const clonedExercise = { ...exerciseToClone, setNumber: nextSetNumber + i };
            newExercises.splice(index + 1 + i, 0, clonedExercise);
        }
        
        setExercises(newExercises);
    };

    const handleRemoveExercise = (index: number) => {
        setExercises(exercises.filter((_, i) => i !== index));
    };

    const handleExerciseChange = (index: number, field: keyof Exercise, value: any) => {
        const updatedExercises = exercises.map((exercise, i) => 
            i === index ? { ...exercise, [field]: value } : exercise
        );
        setExercises(updatedExercises);
    };

    const toggleExerciseExpanded = (index: number) => {
        const newExpanded = new Set(expandedExercises);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedExercises(newExpanded);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!name.trim()) return;

        const validExercises = exercises.filter(ex => ex.name.trim());
        if (validExercises.length === 0) return;

        const workoutData: WorkoutData = {
            exercises: validExercises,
            duration,
            calories,
            type,
            difficulty: 'intermediate'
        };

        // Combine date and time into timestamp
        const timestamp = new Date(`${workoutDate}T${workoutTime}`).getTime();
        
        onSave({
            name: name.trim(),
            description: '',
            imageUrl: '',
            category: 'workout',
            timestamp: timestamp,
            data: workoutData
        });

        // Reset form
        setName('');
        setDuration(30);
        setCalories(200);
        setType('strength');
        setExercises([{ name: '', sets: 1, reps: 10, weight: 0 }]);
        setSelectedTemplate('');
        setShowSaveAsTemplate(false);
        setTemplateName('');
        onClose();
    };

    const headerActions = (
        <>
            <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-sm text-slate-300 hover:text-white transition-colors"
            >
                Cancel
            </button>
            <button
                type="submit"
                form="workout-form"
                disabled={!name.trim() || exercises.filter(ex => ex.name.trim()).length === 0}
                className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {editingItem ? "Update" : "Save"}
            </button>
        </>
    );

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={editingItem ? "Edit Workout" : "Add New Workout"} maxWidth="max-w-4xl" preventClose={true} headerActions={headerActions}>
            <form id="workout-form" onSubmit={handleSubmit} className="space-y-4">

                {/* Template Selection */}
                {!editingItem && availableTemplates.length > 0 && (
                    <div className="p-3 bg-slate-700/30 rounded-lg border border-slate-600">
                        <label htmlFor="templateSelect" className="block text-sm font-medium text-slate-300 mb-2">
                            Start from Template (Optional)
                        </label>
                        <select
                            id="templateSelect"
                            value={selectedTemplate}
                            onChange={(e) => handleTemplateSelection(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">Create from scratch</option>
                            {availableTemplates.map((template) => (
                                <option key={template.id} value={template.id.toString()}>
                                    {template.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Basic Info */}
                <div className="border border-slate-600 rounded-lg bg-slate-700/30">
                    <button
                        type="button"
                        onClick={() => setIsBasicInfoExpanded(!isBasicInfoExpanded)}
                        className="w-full flex items-center justify-between p-3 text-left hover:bg-slate-700/50 transition-colors"
                    >
                        <h3 className="text-base font-medium text-white">Workout Details</h3>
                        <svg className={`w-5 h-5 text-slate-400 transition-transform ${isBasicInfoExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    
                    {isBasicInfoExpanded && (
                        <div className="px-3 pb-3 space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="workoutName" className="block text-sm font-medium text-slate-300 mb-2">
                                        Workout Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="workoutName"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="e.g., Morning Cardio"
                            required
                        />
                                </div>
                                <div>
                                    <label htmlFor="type" className="block text-sm font-medium text-slate-300 mb-2">
                                        Type <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        id="type"
                                        value={type}
                                        onChange={(e) => setType(e.target.value as any)}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="strength">Strength</option>
                                        <option value="cardio">Cardio</option>
                                        <option value="flexibility">Flexibility</option>
                                        <option value="mixed">Mixed</option>
                                    </select>
                                </div>
                            </div>

                            {/* Workout Details */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="duration" className="block text-sm font-medium text-slate-300 mb-2">
                                        Duration (mins) <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        id="duration"
                                        value={duration}
                                        onChange={(e) => setDuration(Number(e.target.value))}
                                        min="1"
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="calories" className="block text-sm font-medium text-slate-300 mb-2">
                                        Calories <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        id="calories"
                                        value={calories}
                                        onChange={(e) => setCalories(Number(e.target.value))}
                                        min="1"
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>

                            {/* Date and Time */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="workoutDate" className="block text-sm font-medium text-slate-300 mb-2">
                                        Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        id="workoutDate"
                                        value={workoutDate}
                                        onChange={(e) => setWorkoutDate(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="workoutTime" className="block text-sm font-medium text-slate-300 mb-2">
                                        Time <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="time"
                                        id="workoutTime"
                                        value={workoutTime}
                                        onChange={(e) => setWorkoutTime(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Exercises */}
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-base font-medium text-white">Exercises <span className="text-red-500">*</span></h3>
                        <button
                            type="button"
                            onClick={handleAddExercise}
                            className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                        >
                            Add Exercise
                        </button>
                    </div>
                    
                    <div className="space-y-3">
                        {exercises.slice().reverse().map((exercise, reverseIndex) => {
                            const index = exercises.length - 1 - reverseIndex;
                            const isExpanded = expandedExercises.has(index);
                            return (
                                <div key={index} className={`${isExpanded ? 'p-3' : 'p-2'} bg-slate-700/50 rounded-lg border border-slate-600`}>
                                    <div className={`flex items-center justify-between ${isExpanded ? 'mb-2' : ''}`}>
                                        <div className="flex items-center gap-2 flex-1">
                                            <button
                                                type="button"
                                                onClick={() => toggleExerciseExpanded(index)}
                                                className="text-slate-400 hover:text-white transition-colors"
                                                title={isExpanded ? "Collapse" : "Expand"}
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    {isExpanded ? (
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    ) : (
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    )}
                                                </svg>
                                            </button>
                                            <span className="text-xs font-medium text-slate-300">Exercise {index + 1}</span>
                                            {!isExpanded && (
                                                <span className="text-xs text-slate-400 ml-2">
                                                    {exercise.name || 'Unnamed exercise'} 
                                                    {exercise.setNumber && ` • Set ${exercise.setNumber}`}
                                                    {exercise.reps && ` • ${exercise.reps} reps`}
                                                    {exercise.weight ? ` • ${exercise.weight} lbs` : ''}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <select
                                                value={cloneCount}
                                                onChange={(e) => setCloneCount(Number(e.target.value))}
                                                className="px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                title="Number of clones"
                                            >
                                                <option value={1}>1</option>
                                                <option value={2}>2</option>
                                                <option value={3}>3</option>
                                            </select>
                                            <button
                                                type="button"
                                                onClick={() => handleCloneExercise(index)}
                                                className="text-blue-400 hover:text-blue-300"
                                                title="Clone exercise"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                </svg>
                                            </button>
                                            {exercises.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveExercise(index)}
                                                    className="text-red-400 hover:text-red-300"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                
                                    {isExpanded && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
                                            <div className="lg:col-span-2">
                                                <label className="block text-xs font-medium text-slate-400 mb-1">Exercise Name *</label>
                                                <input
                                                    type="text"
                                                    value={exercise.name}
                                                    onChange={(e) => handleExerciseChange(index, 'name', e.target.value)}
                                                    placeholder="Exercise name *"
                                                    className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-slate-400 mb-1">Set #</label>
                                                <input
                                                    type="number"
                                                    value={exercise.setNumber || ''}
                                                    onChange={(e) => handleExerciseChange(index, 'setNumber', Number(e.target.value) || undefined)}
                                                    placeholder="Set #"
                                                    min="1"
                                                    className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-slate-400 mb-1">Reps</label>
                                                <input
                                                    type="number"
                                                    value={exercise.reps || ''}
                                                    onChange={(e) => handleExerciseChange(index, 'reps', Number(e.target.value) || undefined)}
                                                    placeholder="Reps"
                                                    min="1"
                                                    className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-slate-400 mb-1">Weight (lbs)</label>
                                                <input
                                                    type="number"
                                                    value={exercise.weight || ''}
                                                    onChange={(e) => handleExerciseChange(index, 'weight', Number(e.target.value) || undefined)}
                                                    placeholder="Weight (lbs)"
                                                    min="0"
                                                    className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Save as Template */}
                {(
                    <div className="p-3 bg-slate-700/30 rounded-lg border border-slate-600">
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-slate-300">Save as Template</h4>
                            <button
                                type="button"
                                onClick={() => setShowSaveAsTemplate(!showSaveAsTemplate)}
                                className="text-xs text-blue-400 hover:text-blue-300"
                            >
                                {showSaveAsTemplate ? 'Cancel' : 'Save Template'}
                            </button>
                        </div>
                        {showSaveAsTemplate && (
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={templateName}
                                    onChange={(e) => setTemplateName(e.target.value)}
                                    placeholder="Template name"
                                    className="flex-1 px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                />
                                <button
                                    type="button"
                                    onClick={saveAsTemplate}
                                    disabled={!templateName.trim()}
                                    className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Save
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Template Creation for Editing */}
                {editingItem && (
                    <div className="pt-4 border-t border-slate-700">
                        <button
                            type="button"
                            onClick={() => {
                                setTemplateName(name + ' Template');
                                setShowSaveAsTemplate(true);
                            }}
                            disabled={!name.trim() || exercises.filter(ex => ex.name.trim()).length === 0}
                            className="px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Create Template
                        </button>
                    </div>
                )}
            </form>
        </Modal>
    );
};

export default AddWorkoutModal;